import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import { selectIsEditMode } from 'features/common/editModeSlice';
import dataValidationService from 'features/ImportData/dataValidationService';
import { CheckedImportDataDetailResponse, CheckedImportMenu } from 'features/Store/models';
import { ChangeEvent, Fragment, useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { MAXINUM_TOTAL_POS_UNREGISTER, displayError, handleServiceError } from 'utils';
import AlertModal from '../AlertModal';
import FeatureTitleBar from '../FeatureTitleBar';
import useGlobalModal from '../GlobalModal/useGlobalModal';
import ReArrangeMenuModal from '../ReArrangeMenuModal/ReArrangeMenuModal';
import './ErrorDataDetailModal.scss';

const COUPON_CLASSIFICATION_KEY: any = {
  0: 'U',
  1: 'C',
  2: 'T',
};

export interface ErrorDataDetailModalProps {
  errorFromFeatureId: number; // 3, 14, 16
  restaurantId?: number;
  counterPatternId?: number;
  onClose?: (isDataChanged: boolean) => void;
}

function ErrorDataDetailModal({
  errorFromFeatureId,
  restaurantId,
  counterPatternId,
  onClose,
}: ErrorDataDetailModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // ref
  const btnReArrangePattern3Ref = useRef<HTMLButtonElement>(null);
  const btnReArrangePattern4Ref = useRef<HTMLButtonElement>(null);

  // state
  const [checkedImportData, setCheckedImportData] = useState<CheckedImportDataDetailResponse>();
  const [showReArrangeMenuModal, setShowReArrangeMenuModal] = useState<boolean>(false);
  const [selectedCheckedImportMenu, setSelectedCheckedImportMenu] = useState<CheckedImportMenu>();
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);

  function handleClose() {
    onClose?.(isDataChanged);
  }

  function fetchErrorImportDataDetail() {
    (async () => {
      try {
        const response = await dataValidationService.getErrorImportDataDetail(restaurantId, counterPatternId);
        setCheckedImportData(response.data);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  useEffect(() => {
    fetchErrorImportDataDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // handle status button actions
  useEffect(() => {
    if (btnReArrangePattern3Ref.current) btnReArrangePattern3Ref.current.disabled = true;
    if (btnReArrangePattern4Ref.current) btnReArrangePattern4Ref.current.disabled = true;

    // clear all checkboxes pattern 3
    const chkPattern3SelectedElements = document.querySelectorAll<HTMLInputElement>(
      '.chk-pattern3-selected input:checked'
    );
    chkPattern3SelectedElements.forEach(function (chkPattern3SelectedElement) {
      chkPattern3SelectedElement.checked = false;
    });

    // clear all checkboxes pattern 4
    const chkPattern4SelectedElements = document.querySelectorAll<HTMLInputElement>(
      '.chk-pattern4-selected input:checked'
    );
    chkPattern4SelectedElements.forEach(function (chkPattern4SelectedElement) {
      chkPattern4SelectedElement.checked = false;
    });
  }, [checkedImportData]);

  function getErrorOverViewInfo() {
    let message = '';

    switch (errorFromFeatureId) {
      case 3:
        message = t('ErrorDataDetailModal.overview_error_info2');
        break;
      case 14:
        message = t('ErrorDataDetailModal.overview_error_info3');
        break;
      case 16:
        message = t('ErrorDataDetailModal.overview_error_info1');
        break;
      default:
        break;
    }

    return message;
  }
  const errorOverviewMessage = getErrorOverViewInfo();

  function getShortNameCouponClassification(couponClassification: number) {
    return COUPON_CLASSIFICATION_KEY[couponClassification] ?? '';
  }

  function renderErrorSectionPattern1() {
    const errorList = checkedImportData?.unregistered_poscode_cds || [];
    if (errorList.length > 0) {
      return (
        <div className="error-section">
          {errorList.length <= MAXINUM_TOTAL_POS_UNREGISTER && (
            <div
              className="error-section-summary"
              dangerouslySetInnerHTML={{
                __html: t('ErrorDataDetailModal.warn_has_unregister_default').replace(
                  '{0}',
                  errorList.length.toString()
                ),
              }}
            ></div>
          )}
          {errorList.length > MAXINUM_TOTAL_POS_UNREGISTER && (
            <div
              className="error-section-summary border-bottom"
              dangerouslySetInnerHTML={{
                __html: t('ErrorDataDetailModal.warn_has_unregister_over_limit').replace(
                  '{0}',
                  MAXINUM_TOTAL_POS_UNREGISTER.toString()
                ),
              }}
            ></div>
          )}
          {errorList.length <= MAXINUM_TOTAL_POS_UNREGISTER && (
            <div className="error-section-detail">
              <div className="sticky-table flex-grow-1 flex-shrink-1" style={{ maxHeight: '360px' }}>
                <Table hover>
                  <thead>
                    <tr>
                      <th
                        style={{ width: '35%' }}
                        dangerouslySetInnerHTML={{ __html: t('ErrorDataDetailModal.menu_or_menu_subtitle_cd') }}
                      ></th>
                      <th style={{ width: '25%' }}>{t('ErrorDataDetailModal.menu_name')}</th>
                      <th style={{ width: '15%' }}>{t('ErrorDataDetailModal.pos_cd')}</th>
                      <th style={{ width: '25%' }}>{t('ErrorDataDetailModal.pos_name')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errorList.map(function (posCodeCd) {
                      return (
                        <tr key={posCodeCd}>
                          <td className="text-center"></td>
                          <td className="text-center"></td>
                          <td className="text-center">{posCodeCd}</td>
                          <td className="text-center"></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </div>
      );
    }

    return null;
  }

  // function renderErrorSectionPattern2() {
  //   const errorList = checkedImportData?.menu_invalid_pos_codes || [];
  //   if (errorList.length > 0) {
  //     return (
  //       <div className="error-section">
  //         <div
  //           className="error-section-summary"
  //           dangerouslySetInnerHTML={{ __html: t('ErrorDataDetailModal.warn_invalid_pos_code') }}
  //         ></div>
  //         <div className="error-section-detail">
  //           <div className="sticky-table flex-grow-1 flex-shrink-1" style={{ maxHeight: '360px' }}>
  //             <Table hover>
  //               <thead>
  //                 <tr>
  //                   {/* <th style={{ width: '5%' }}>{t('ErrorDataDetailModal.selection')}</th> */}
  //                   <th
  //                     style={{ width: '35%' }}
  //                     dangerouslySetInnerHTML={{ __html: t('ErrorDataDetailModal.menu_or_menu_subtitle_cd') }}
  //                   ></th>
  //                   <th style={{ width: '25%' }}>{t('ErrorDataDetailModal.menu_name')}</th>
  //                   <th style={{ width: '15%' }}>{t('ErrorDataDetailModal.pos_cd')}</th>
  //                   <th style={{ width: '25%' }}>{t('ErrorDataDetailModal.pos_name')}</th>
  //                 </tr>
  //               </thead>
  //               <tbody>
  //                 {errorList.map(function (errorItem, errorItemIndex) {
  //                   const shortNameCouponClassification = getShortNameCouponClassification(
  //                     errorItem.coupon_classification
  //                   );
  //                   return (
  //                     <Fragment key={errorItem.id}>
  //                       <tr className="divide">
  //                         {/* <td rowSpan={errorItem.menu_menu_details.length + 1} className="text-center">
  //                           <Form.Check className="chk-selected" type="checkbox" />
  //                         </td> */}
  //                         <td rowSpan={errorItem.menu_menu_details.length + 1} className="text-center">
  //                           {errorItem.food_system_menu.cd}
  //                           {shortNameCouponClassification}
  //                         </td>
  //                         <td rowSpan={errorItem.menu_menu_details.length + 1} className="text-center">
  //                           {errorItem.food_system_menu.name}
  //                         </td>
  //                       </tr>
  //                       {errorItem.menu_menu_details.map((menuDetail, indexMenuDetail) => {
  //                         return (
  //                           <Fragment key={errorItem.id + '-' + menuDetail.id}>
  //                             <tr className={errorItemIndex !== 0 && indexMenuDetail === 0 ? 'divide' : ''}>
  //                               <td className="text-center">{menuDetail.pos_code.cd}</td>
  //                               <td className="text-center">{menuDetail.name}</td>
  //                             </tr>
  //                           </Fragment>
  //                         );
  //                       })}
  //                     </Fragment>
  //                   );
  //                 })}
  //               </tbody>
  //             </Table>
  //           </div>
  //         </div>
  //       </div>
  //     );
  //   }

  //   return null;
  // }

  function handleCloseReArrangeMenuModal(isRearrangeData: boolean) {
    setShowReArrangeMenuModal(false);
    setSelectedCheckedImportMenu(undefined);

    if (isRearrangeData) {
      fetchErrorImportDataDetail();
      setIsDataChanged(true);

      setTimeout(() => {
        showGlobalModal(() => (
          <AlertModal title={t('App.info')} message={t('ErrorDataDetailModal.warn_rearrange_menu_successful')} />
        ));
      }, 300);
    }
  }

  // function handleChkPattern3SelectedChange(event: ChangeEvent<HTMLInputElement>) {
  //   if (event.target.checked) {
  //     const chkPattern3SelectedElements = document.querySelectorAll<HTMLInputElement>(
  //       '.chk-pattern3-selected input:checked'
  //     );
  //     chkPattern3SelectedElements.forEach(function (chkPattern3SelectedElement) {
  //       chkPattern3SelectedElement.checked = false;
  //     });
  //     event.target.checked = true;
  //     if (btnReArrangePattern3Ref.current) btnReArrangePattern3Ref.current.disabled = false;
  //   } else {
  //     if (btnReArrangePattern3Ref.current) btnReArrangePattern3Ref.current.disabled = true;
  //   }
  // }

  // function handleBtnReArrangePattern3Click() {
  //   const errorList = checkedImportData?.menu_has_poscode_in_main_and_sub_menus || [];
  //   if (errorList.length > 0) {
  //     const chkPattern3SelectedElement = document.querySelector<HTMLInputElement>(
  //       '.chk-pattern3-selected input:checked'
  //     );
  //     if (chkPattern3SelectedElement) {
  //       const selectedIndex = Number(chkPattern3SelectedElement.value);
  //       setShowReArrangeMenuModal(true);
  //       setSelectedCheckedImportMenu(JSON.parse(JSON.stringify(errorList[selectedIndex])));
  //     }
  //   }
  // }

  // function renderErrorSectionPattern3() {
  //   const errorList = checkedImportData?.menu_has_poscode_in_main_and_sub_menus || [];
  //   if (errorList.length > 0) {
  //     return (
  //       <div className="error-section">
  //         <div className="text-end mb-2">
  //           <Button ref={btnReArrangePattern3Ref} onClick={handleBtnReArrangePattern3Click}>
  //             {t('ErrorDataDetailModal.rearrange_menu')}
  //           </Button>
  //         </div>
  //         <div
  //           className="error-section-summary"
  //           dangerouslySetInnerHTML={{ __html: t('ErrorDataDetailModal.warn_menu_has_poscode_in_main_and_sub_menu') }}
  //         ></div>
  //         <div className="error-section-detail">
  //           <div className="sticky-table flex-grow-1 flex-shrink-1" style={{ maxHeight: '360px' }}>
  //             <Table hover>
  //               <thead>
  //                 <tr>
  //                   <th style={{ width: '5%' }}>{t('ErrorDataDetailModal.selection')}</th>
  //                   <th
  //                     style={{ width: '30%' }}
  //                     dangerouslySetInnerHTML={{ __html: t('ErrorDataDetailModal.menu_or_menu_subtitle_cd') }}
  //                   ></th>
  //                   <th style={{ width: '25%' }}>{t('ErrorDataDetailModal.menu_name')}</th>
  //                   <th style={{ width: '15%' }}>{t('ErrorDataDetailModal.pos_cd')}</th>
  //                   <th style={{ width: '25%' }}>{t('ErrorDataDetailModal.pos_name')}</th>
  //                 </tr>
  //               </thead>
  //               <tbody>
  //                 {errorList.map(function (errorItem, errorItemIndex) {
  //                   const shortNameCouponClassification = getShortNameCouponClassification(
  //                     errorItem.coupon_classification
  //                   );
  //                   return (
  //                     <Fragment key={errorItem.id}>
  //                       <tr className="divide">
  //                         <td rowSpan={errorItem.menu_menu_details.length + 1} className="text-center">
  //                           <Form.Check
  //                             className="chk-pattern3-selected"
  //                             type="checkbox"
  //                             value={errorItemIndex}
  //                             onChange={handleChkPattern3SelectedChange}
  //                           />
  //                         </td>
  //                         <td rowSpan={errorItem.menu_menu_details.length + 1} className="text-center">
  //                           {errorItem.food_system_menu.cd}
  //                           {shortNameCouponClassification}
  //                         </td>
  //                         <td rowSpan={errorItem.menu_menu_details.length + 1} className="text-center">
  //                           {errorItem.food_system_menu.name}
  //                         </td>
  //                       </tr>
  //                       {errorItem.menu_menu_details.map((menuDetail, indexMenuDetail) => {
  //                         return (
  //                           <Fragment key={errorItem.id + '-' + menuDetail.id}>
  //                             <tr className={errorItemIndex !== 0 && indexMenuDetail === 0 ? 'divide' : ''}>
  //                               <td className="text-center">{menuDetail.pos_code.cd}</td>
  //                               <td className="text-center">{menuDetail.name}</td>
  //                             </tr>
  //                           </Fragment>
  //                         );
  //                       })}
  //                     </Fragment>
  //                   );
  //                 })}
  //               </tbody>
  //             </Table>
  //           </div>
  //         </div>
  //       </div>
  //     );
  //   }

  //   return null;
  // }

  function handleChkPattern4SelectedChange(event: ChangeEvent<HTMLInputElement>) {
    if (event.target.checked) {
      const chkPattern4SelectedElements = document.querySelectorAll<HTMLInputElement>(
        '.chk-pattern4-selected input:checked'
      );
      chkPattern4SelectedElements.forEach(function (chkPattern4SelectedElement) {
        chkPattern4SelectedElement.checked = false;
      });
      event.target.checked = true;
      if (btnReArrangePattern4Ref.current) btnReArrangePattern4Ref.current.disabled = false;
    } else {
      if (btnReArrangePattern4Ref.current) btnReArrangePattern4Ref.current.disabled = true;
    }
  }

  function handleBtnReArrangePattern4Click() {
    const errorList = checkedImportData?.menu_multiple_registered_poscodes || [];
    if (errorList.length > 0) {
      const chkPattern4SelectedElement = document.querySelector<HTMLInputElement>(
        '.chk-pattern4-selected input:checked'
      );
      if (chkPattern4SelectedElement) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const selectedIndex = Number(chkPattern4SelectedElement.value);
        setShowReArrangeMenuModal(true);
        setSelectedCheckedImportMenu(JSON.parse(JSON.stringify(errorList[selectedIndex])));
      }
    }
  }

  function renderErrorSectionPattern4() {
    const errorList = checkedImportData?.menu_multiple_registered_poscodes || [];
    if (errorList.length > 0) {
      return (
        <div className="error-section">
          <div className="text-end mb-2">
            <Button ref={btnReArrangePattern4Ref} onClick={handleBtnReArrangePattern4Click}>
              {t('ErrorDataDetailModal.rearrange_menu')}
            </Button>
          </div>
          <div
            className="error-section-summary"
            dangerouslySetInnerHTML={{ __html: t('ErrorDataDetailModal.warn_has_multiple_registered') }}
          ></div>
          <div className="error-section-detail">
            <div className="sticky-table flex-grow-1 flex-shrink-1" style={{ maxHeight: '360px' }}>
              <Table hover>
                <thead>
                  <tr>
                    <th style={{ width: '5%' }}>{t('ErrorDataDetailModal.selection')}</th>
                    <th
                      style={{ width: '30%' }}
                      dangerouslySetInnerHTML={{ __html: t('ErrorDataDetailModal.menu_or_menu_subtitle_cd') }}
                    ></th>
                    <th style={{ width: '25%' }}>{t('ErrorDataDetailModal.menu_name')}</th>
                    <th style={{ width: '15%' }}>{t('ErrorDataDetailModal.pos_cd')}</th>
                    <th style={{ width: '25%' }}>{t('ErrorDataDetailModal.pos_name')}</th>
                  </tr>
                </thead>
                <tbody>
                  {errorList.map(function (errorItem, errorItemIndex) {
                    const shortNameCouponClassification = getShortNameCouponClassification(
                      errorItem.coupon_classification
                    );
                    return (
                      <Fragment key={errorItem.id}>
                        <tr className="divide">
                          <td rowSpan={errorItem.menu_menu_details.length + 1} className="text-center">
                            <Form.Check
                              className="chk-pattern4-selected"
                              type="checkbox"
                              value={errorItemIndex}
                              onChange={handleChkPattern4SelectedChange}
                            />
                          </td>
                          <td rowSpan={errorItem.menu_menu_details.length + 1} className="text-center">
                            {errorItem.food_system_menu.cd}
                            {shortNameCouponClassification}
                          </td>
                          <td rowSpan={errorItem.menu_menu_details.length + 1} className="text-center">
                            {errorItem.food_system_menu.name}
                          </td>
                        </tr>
                        {errorItem.menu_menu_details.map((menuDetail, indexMenuDetail) => {
                          return (
                            <Fragment key={errorItem.id + '-' + menuDetail.id}>
                              <tr className={errorItemIndex !== 0 && indexMenuDetail === 0 ? 'divide' : ''}>
                                <td className="text-center">{menuDetail.pos_code.cd}</td>
                                <td className="text-center">{menuDetail.name}</td>
                              </tr>
                            </Fragment>
                          );
                        })}
                      </Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      );
    }

    return null;
  }

  function renderErrorSectionPattern5() {
    const errorList = checkedImportData?.poscode_used_in_multiple_menu_subtitle_menus || [];
    if (errorList.length > 0) {
      return (
        <div className="error-section">
          <div
            className="error-section-summary"
            dangerouslySetInnerHTML={{
              __html: t('ErrorDataDetailModal.warn_poscode_used_in_multiple_menu_subtitle_menu'),
            }}
          ></div>
          <div className="error-section-detail">
            <div className="sticky-table flex-grow-1 flex-shrink-1" style={{ maxHeight: '360px' }}>
              <Table hover>
                <thead>
                  <tr>
                    <th
                      style={{ width: '35%' }}
                      dangerouslySetInnerHTML={{ __html: t('ErrorDataDetailModal.menu_or_menu_subtitle_cd') }}
                    ></th>
                    <th style={{ width: '25%' }}>{t('ErrorDataDetailModal.menu_name')}</th>
                    <th style={{ width: '15%' }}>{t('ErrorDataDetailModal.pos_cd')}</th>
                    <th style={{ width: '25%' }}>{t('ErrorDataDetailModal.pos_name')}</th>
                  </tr>
                </thead>
                <tbody>
                  {errorList.map(function (errorItem, errorItemIndex) {
                    const shortNameCouponClassification = getShortNameCouponClassification(
                      errorItem.menus?.[0].coupon_classification
                    );
                    return (
                      <Fragment key={errorItem.id}>
                        <tr className="divide">
                          <td className="text-center">
                            {errorItem.menus?.[0].food_system_menu.cd}
                            {shortNameCouponClassification}
                          </td>
                          <td className="text-center">{errorItem.menus?.[0].food_system_menu.name}</td>
                          <td rowSpan={errorItem.menus.length} className="text-center">
                            {errorItem.cd}
                          </td>
                          <td rowSpan={errorItem.menus.length} className="text-center">
                            {errorItem.menu_details?.[0].name || '-'}
                          </td>
                        </tr>
                        {errorItem.menus.map(function (menu, menuIndex) {
                          const shortNameCouponClassificationItem = getShortNameCouponClassification(
                            menu.coupon_classification
                          );
                          if (menuIndex === 0) return undefined;
                          return (
                            <tr key={errorItem.id + '-' + menu.id}>
                              <td className="text-center">
                                {menu.food_system_menu.cd}
                                {shortNameCouponClassificationItem}
                              </td>
                              <td className="text-center">{menu.food_system_menu.name}</td>
                            </tr>
                          );
                        })}
                      </Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      );
    }

    return null;
  }

  const errorSectionPattern1 = renderErrorSectionPattern1();
  // const errorSectionPattern2 = renderErrorSectionPattern2();
  // const errorSectionPattern3 = renderErrorSectionPattern3();
  const errorSectionPattern4 = renderErrorSectionPattern4();
  const errorSectionPattern5 = renderErrorSectionPattern5();

  return (
    <>
      <Modal
        size="lg"
        dialogClassName="error-data-detail-modal"
        className="alert-modal"
        show
        backdrop="static"
        centered
        onHide={handleClose}
      >
        <Modal.Header
          closeButton
          className={`bg-primary1 justify-content-center ${isEditMode ? 'reedit-mode-wrapper' : ''}`}
        >
          <FeatureTitleBar title={t('ErrorDataDetailModal.head_title')}></FeatureTitleBar>
        </Modal.Header>

        <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
          <div className="mb-3">
            <div className="error-overview-box alert alert-danger mb-3">{errorOverviewMessage}</div>
            {restaurantId && (
              <>
                <div className="mb-1">
                  <strong>
                    {t('ErrorDataDetailModal.store_cd')}: {checkedImportData?.restaurant_cd}
                  </strong>
                </div>
                <div>
                  <strong>
                    {t('ErrorDataDetailModal.store_name')}: {checkedImportData?.restaurant_name}
                  </strong>
                </div>
              </>
            )}
          </div>

          <div className="error-section-list">
            {errorSectionPattern1}
            {/* {errorSectionPattern2} */}
            {/* {errorSectionPattern3} */}
            {errorSectionPattern4}
            {errorSectionPattern5}
          </div>
        </Modal.Body>

        {/* <Modal.Footer className="justify-content-center">
        <Button variant="light" onClick={handleClose}>
          {t('Alert_Modal.close')}
        </Button>
      </Modal.Footer> */}
      </Modal>

      {showReArrangeMenuModal &&
        selectedCheckedImportMenu &&
        createPortal(
          <ReArrangeMenuModal menu={selectedCheckedImportMenu} onClose={handleCloseReArrangeMenuModal} />,
          document.body
        )}
    </>
  );
}

export default ErrorDataDetailModal;
